import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api-client';
import { formatDate, formatPercent, classNames } from '@/lib/utils';
import type { Store, Audit } from '@/types';

// Mock de auditorias para esta loja
const MOCK_AUDITS: (Audit & { compliance?: number })[] = [
    {
        id: 'audit-001',
        storeId: 'store-001',
        userId: 'user-admin',
        auditDate: '2026-01-18',
        status: 'completed',
        compliance: 92,
    },
    {
        id: 'audit-002',
        storeId: 'store-001',
        userId: 'user-admin',
        auditDate: '2026-01-10',
        status: 'completed',
        compliance: 85,
    },
    {
        id: 'audit-003',
        storeId: 'store-001',
        userId: 'user-admin',
        auditDate: '2026-01-03',
        status: 'completed',
        compliance: 78,
    },
];

export function StoreDetail() {
    const { storeId } = useParams<{ storeId: string }>();
    const navigate = useNavigate();

    const [store, setStore] = useState<Store | null>(null);
    const [audits, setAudits] = useState<(Audit & { compliance?: number })[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function loadData() {
            setIsLoading(true);

            // Carregar loja
            const storeResponse = await api.get<Store>(`/stores/${storeId}`);
            if (storeResponse.success && storeResponse.data) {
                setStore(storeResponse.data);
            }

            // Mock audits
            setAudits(MOCK_AUDITS.filter(a => a.storeId === storeId));

            setIsLoading(false);
        }

        if (storeId) {
            loadData();
        }
    }, [storeId]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (!store) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
                <Card className="max-w-sm w-full">
                    <CardContent className="text-center py-8">
                        <p className="text-content-muted mb-4">Loja não encontrada</p>
                        <Button onClick={() => navigate(-1)}>Voltar</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const avgCompliance = audits.length > 0
        ? audits.reduce((acc, a) => acc + (a.compliance || 0), 0) / audits.length
        : 0;

    return (
        <div className="min-h-screen bg-page-bg pb-20">
            {/* Header */}
            <header className="bg-brand-header text-content-inverse sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-1 hover:bg-white/10 rounded"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="flex-1">
                            <h1 className="font-semibold">{store.name}</h1>
                            <p className="text-sm opacity-80">{store.city}, {store.state}</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
                {/* Info da loja */}
                <Card>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-content-muted">Código</p>
                                <p className="font-medium text-content-title">{store.code || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-content-muted">Região</p>
                                <p className="font-medium text-content-title">{store.region || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-content-muted">Backlight</p>
                                <p className={classNames(
                                    'font-medium',
                                    store.hasBacklight ? 'text-status-warning' : 'text-content-muted'
                                )}>
                                    {store.hasBacklight ? 'Sim ✓' : 'Não'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-content-muted">Status</p>
                                <p className={classNames(
                                    'font-medium',
                                    store.status === 'active' ? 'text-status-success' : 'text-status-danger'
                                )}>
                                    {store.status === 'active' ? 'Ativa' : 'Inativa'}
                                </p>
                            </div>
                        </div>
                        {store.address && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <p className="text-sm text-content-muted">Endereço</p>
                                <p className="text-content-body">{store.address}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Compliance média */}
                <Card>
                    <CardContent>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-content-muted">Compliance Média</span>
                            <span className={classNames(
                                'text-2xl font-bold',
                                avgCompliance >= 85 ? 'text-status-success' :
                                    avgCompliance >= 70 ? 'text-status-warning' :
                                        'text-status-danger'
                            )}>
                                {formatPercent(avgCompliance, 0)}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className={classNames(
                                    'h-2 rounded-full transition-all',
                                    avgCompliance >= 85 ? 'bg-status-success' :
                                        avgCompliance >= 70 ? 'bg-status-warning' :
                                            'bg-status-danger'
                                )}
                                style={{ width: `${avgCompliance}%` }}
                            />
                        </div>
                        <p className="text-sm text-content-muted mt-2">
                            Baseado em {audits.length} {audits.length === 1 ? 'auditoria' : 'auditorias'}
                        </p>
                    </CardContent>
                </Card>

                {/* Histórico de auditorias */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-content-title">Histórico de Auditorias</h3>
                        <span className="text-sm text-content-muted">{audits.length} registros</span>
                    </div>

                    {audits.length === 0 ? (
                        <Card>
                            <CardContent className="text-center py-8">
                                <svg className="w-12 h-12 text-content-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <p className="text-content-muted">Nenhuma auditoria registrada</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-2">
                            {audits.map((audit) => (
                                <Card key={audit.id}>
                                    <CardContent>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-content-title">
                                                    {formatDate(audit.auditDate)}
                                                </p>
                                                <p className="text-sm text-content-muted capitalize">
                                                    {audit.status === 'completed' ? 'Concluída' :
                                                        audit.status === 'pending' ? 'Pendente' : 'Rascunho'}
                                                </p>
                                            </div>
                                            {audit.compliance !== undefined && (
                                                <span className={classNames(
                                                    'text-lg font-bold',
                                                    audit.compliance >= 85 ? 'text-status-success' :
                                                        audit.compliance >= 70 ? 'text-status-warning' :
                                                            'text-status-danger'
                                                )}>
                                                    {audit.compliance}%
                                                </span>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                {/* Botão de nova auditoria */}
                <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={() => navigate(`/audit/${storeId}`)}
                >
                    Nova Auditoria
                </Button>
            </main>
        </div>
    );
}

export default StoreDetail;
